/* eslint-disable jsx-a11y/mouse-events-have-key-events */
import * as React from 'react';

import { Flex } from '@chakra-ui/react';
import styled from '@emotion/styled';
import { IconButton, makeStyles } from '@material-ui/core';

import { TextBodyMedium } from 'uiToolkitV1/Typography';

import { combine } from '../utils';

const useStyles = makeStyles((theme) => ({
  leftBorderRadius: {
    borderRadius: '50% 0 0 50%',
  },
  rightBorderRadius: {
    borderRadius: '0 50% 50% 0',
  },
  buttonContainer: {
    display: 'flex',
  },
  button: {
    height: 40,
    width: 40,
    padding: 0,
  },
  buttonText: {
    lineHeight: 1.6,
  },
  outlined: {
    border: `1px solid ${theme.palette.primary.dark}`,
  },
  filled: {
    '&:hover': {
      backgroundColor: '#0A7795',
    },
    backgroundColor: '#0D819F',
  },
  highlighted: {
    backgroundColor: '#cae8f0',
  },
  contrast: {
    color: 'green',
  },
}));

/**
 * Calculates the text color for a day in the date picker based on its state
 * @param {Object} params - The state parameters for the day
 * @param {boolean} params.isStart - Whether this is the start of a selected range
 * @param {boolean} params.isEnd - Whether this is the end of a selected range
 * @param {boolean} params.isToday - Whether this is today's date
 * @param {boolean} params.highlighted - Whether this day is highlighted (in range)
 * @param {boolean} params.disabled - Whether this day is disabled
 * @returns {string} The color value to use for the day text
 */
export const getDayTextColor = ({ isStart, isEnd, isToday, highlighted, disabled }) => {
  if (isStart || isEnd) {
    return '#fff';
  }

  if (disabled && highlighted) {
    return 'text.g3';
  }

  if (isToday || highlighted) {
    return 'primary.1565BC';
  }

  if (!disabled) {
    return 'text.g1';
  }

  return 'text.g3';
};

const Day = ({
  startOfRange,
  endOfRange,
  isOutOfRangeDay,
  highlighted,
  outlined,
  filled,
  onClick,
  onHover,
  value,
  isToday,
  isStart,
  isEnd,
  isSuffixPrefixDay = false,
}) => {
  const classes = useStyles();

  const disabled = isOutOfRangeDay || isSuffixPrefixDay;

  return (
    <div
      className={combine(
        classes.buttonContainer,
        startOfRange && classes.leftBorderRadius,
        endOfRange && classes.rightBorderRadius,
        !isOutOfRangeDay && highlighted && classes.highlighted
      )}
      style={{ marginTop: '8px' }}
    >
      <StyledDayButton
        className={combine(classes.button, !isOutOfRangeDay && filled && classes.filled)}
        disabled={isOutOfRangeDay}
        isOutOfRangeDay={isOutOfRangeDay}
        onClick={onClick}
        onMouseOver={onHover}
        isStart={isStart}
        isEnd={isEnd}
        highlighted={highlighted}
      >
        <Flex flexDir="column" alignItems="center" position="relative">
          <TextBodyMedium
            bold
            color={getDayTextColor({ isStart, isEnd, isToday, highlighted, disabled })}
            className={combine(classes.buttonText, !disabled && filled && classes.contrast)}
          >
            {value}
          </TextBodyMedium>
          {isToday && <Dot />}
        </Flex>
      </StyledDayButton>
    </div>
  );
};

const Dot = styled.div`
  width: 3px;
  height: 3px;
  border-radius: 50%;
  background-color: ${(props) => props.theme.colors.functional['1565BC']};
  position: absolute;
  bottom: 0;
`;

const StyledDayButton = styled(IconButton)`
  background-color: ${(props) => {
    if (props.isOutOfRangeDay && (props.isEnd || props.isStart)) {
      return '#c62c02 !important';
    }
    if (props.isOutOfRangeDay && props.highlighted) {
      return '#ffb9a63D !important';
    }
    return props.isStart || props.isEnd ? props.theme.colors.primary['1565BC'] : 'transparent';
  }};
  border-radius: ${(props) => (props.isStart ? '4px 0px 0px 4px' : props.isEnd ? ' 0px 4px 4px 0px' : '0')};
`;

export default Day;
